import { useCallback, useEffect, useState } from "react";
import type { ObjectiveProgress, StudentDetail } from "@talent/protocol";
import {
  ClassError,
  assignObjective,
  fetchObjectives,
  fetchStudent,
  removeStudent,
  renameStudent,
  type Catalogue,
} from "../class-api.js";
import { COPY, fill, objectiveLabel, strandLabel, taskLabel } from "../copy.js";
import { fa, faDate } from "../format.js";
import { LevelBar } from "./level-bar.js";
import { ScreenHeader } from "./screen-header.js";
import { useClasses } from "./class-context.js";

/**
 * One student, in full.
 *
 * **Rebuilt around what a teacher opens it to find out**, which the first version had in the right
 * order and the wrong shapes. Everything on it was the same white card at the same weight —
 * objectives, games and the remove button alike — so a teacher scanning had nowhere to land; the
 * admin actions sat at the top of a screen nobody opens to rename anybody; the objectives were a
 * flat list of names when a teacher thinks in areas of the week; and the objectives a child has not
 * started were left off entirely, so the screen could say what had been done and never what was
 * left.
 *
 * The order now runs: how much have they played, what should they do next, how far has each area
 * got, and only then the games. **A game level is evidence, not the answer**, which is why it is
 * last and small.
 *
 * **Three states, never a percentage.** "Sixty percent of the way through counting to five" is a
 * number with no referent — there is no sixtieth of an idea. What is shown instead is how long the
 * child's best run of correct answers is against the run that counts as learnt, drawn as dots
 * because a two-of-three sentence has to be decoded and two filled dots do not.
 */

const CARD = "rounded-lg bg-card px-md py-md shadow-card";
const ACTION = "rounded-pill bg-grape px-md py-sm text-small text-card disabled:opacity-60";
const QUIET = "rounded-pill bg-card px-md py-sm text-small shadow-card disabled:opacity-60";
const FIELD =
  "w-full rounded-md border border-edge bg-card px-md py-sm text-body outline-none focus-visible:border-grape";

/**
 * Trials behind an objective that is still not learnt, before the screen marks it.
 *
 * Twelve, because that is one sitting (`maxTrials`): a child who has spent a whole afternoon inside
 * one objective and has not yet strung the run together is where the teacher's next lesson is. Under
 * that it is simply an objective in progress, which is most of them.
 *
 * The mark says what the RECORD holds and never what the child is: how many turns went in and that
 * the run has not happened yet is a fact, where anything shaped like "weak at this" is a judgement
 * and has no place on a screen about a five-year-old (v2 §19). The sentence itself is in `copy.ts`.
 */
const STUCK_AFTER_TRIALS = 12;

type State =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "ready"; detail: StudentDetail; catalogue: Catalogue | null };

export function StudentScreen({
  studentId,
  onBack,
  onSignedOut,
  onRemoved,
}: {
  studentId: string;
  onBack: () => void;
  onSignedOut: () => void;
  /** Called after a removal, because this screen is about a student who no longer exists. */
  onRemoved: () => void;
}) {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [panel, setPanel] = useState<"none" | "goal" | "edit" | "remove">("none");
  const [openStrand, setOpenStrand] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      /**
       * The catalogue comes with the student, and its failure is not the student's.
       *
       * It is what carries the STRAND each objective belongs to — the student payload has progress
       * and no grouping — so without it the bars cannot be drawn. If it fails the rest of the screen
       * still renders, because a teacher who cannot see the section bars can still see the child.
       */
      const [detail, catalogue] = await Promise.all([
        fetchStudent(studentId),
        fetchObjectives().catch(() => null),
      ]);
      setState({ kind: "ready", detail, catalogue });
    } catch (err) {
      if (err instanceof ClassError && err.failure === "unauthenticated") {
        onSignedOut();
        return;
      }
      /**
       * A forbidden id is reported as a failure, not redirected.
       *
       * It means the link belongs to another teacher's class — and quietly bouncing to the roster
       * would look like the app losing its place rather than refusing.
       */
      setState({
        kind: "failed",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [studentId, onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") {
    return <p className="p-lg text-center text-muted">{COPY.teacher.loading}</p>;
  }
  if (state.kind === "failed") {
    return (
      <div className="p-lg text-center">
        <p className="text-muted">{fill(COPY.teacher.failed, { message: state.message })}</p>
      </div>
    );
  }

  const { student, objectives, games } = state.detail;
  const name = [student.firstName, student.lastName].filter(Boolean).join(" ");
  const progressOf = new Map(objectives.map((o) => [o.objectiveId, o]));

  /**
   * The strands, in catalogue order, which is teaching order.
   *
   * Built from the CATALOGUE rather than from the student's progress, so an area the child has not
   * touched still gets a row, reading none-of-fifteen. A screen assembled from what happened can
   * only ever show what happened.
   */
  const strands: { strand: string; entries: ObjectiveProgress[] }[] = [];
  for (const objective of state.catalogue?.objectives ?? []) {
    const progress = progressOf.get(objective.id);
    if (!progress) continue;
    const row = strands.find((s) => s.strand === objective.strand);
    if (row) row.entries.push(progress);
    else strands.push({ strand: objective.strand, entries: [progress] });
  }

  return (
    <div className="mx-auto min-h-full max-w-[560px] px-lg pt-lg pb-xxl">
      <ScreenHeader title={name} onBack={onBack} backLabel={COPY.teacher.student.back} />

      <p className="-mt-sm mb-lg text-small text-muted">
        {student.lastPlayedAt
          ? fill(COPY.teacher.roster.lastPlayed, { when: faDate(student.lastPlayedAt) })
          : COPY.teacher.student.neverPlayed}
      </p>

      {/*
        * Three numbers, before anything else.
        *
        * They were one grey sentence. A teacher wants to know how much this child has actually
        * played before they read a word about what they learnt, because everything below means
        * something different after twelve sittings than after one.
        */}
      <div className="mb-lg grid grid-cols-3 gap-sm">
        <Stat value={student.sessions} label={COPY.teacher.student.statSessions} />
        <Stat value={student.daysPlayed} label={COPY.teacher.student.statDays} />
        <Stat value={student.masteredObjectives} label={COPY.teacher.student.statMastered} />
      </div>

      <NextCard
        detail={state.detail}
        studentId={studentId}
        name={student.firstName ?? name}
        onGiven={load}
      />

      {student.lastPlayedAt === null && (
        <p className="mb-lg text-small text-muted">{COPY.teacher.student.neverPlayedHint}</p>
      )}

      {strands.length > 0 && (
        <section className="mb-lg">
          <h2 className="mb-sm text-heading">{COPY.teacher.student.strandsHeading}</h2>
          <div className={CARD}>
            {strands.map(({ strand, entries }) => (
              <StrandRow
                key={strand}
                strand={strand}
                entries={entries}
                open={openStrand === strand}
                onToggle={() => setOpenStrand(openStrand === strand ? null : strand)}
              />
            ))}
          </div>
        </section>
      )}

      {games.length > 0 && (
        <section className="mb-lg">
          <h2 className="mb-sm text-heading">{COPY.teacher.student.gamesHeading}</h2>
          <div className={CARD}>
            {games.map((game, i) => (
              <div
                key={game.family}
                className={i > 0 ? "border-t border-edge pt-sm mt-sm" : undefined}
              >
                <span className="flex items-baseline justify-between gap-sm">
                  <span className="text-body">{taskLabel(game.taskId)}</span>
                  <span className="shrink-0 text-caption text-muted tabular-nums">
                    {fill(COPY.teacher.student.level, {
                      reached: fa(game.reached),
                      max: fa(game.max),
                    })}
                  </span>
                </span>
                <LevelBar reached={game.reached} max={game.max} />
                <span className="mt-1 block text-caption text-muted">
                  {fill(COPY.teacher.student.accuracy, {
                    correct: fa(game.correct),
                    total: fa(game.trials),
                  })}
                  {game.lastPlayedAt && ` · ${faDate(game.lastPlayedAt)}`}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/*
        * The three deliberate acts, together and quiet at the bottom.
        *
        * Handing out an arbitrary goal belongs here rather than beside the suggestion: the button
        * above is "give them the next thing", which is the common case, and this is "I know
        * something you do not", which is rarer and should not compete with it. Renaming and removing
        * are monthly at most and had the best position on the screen.
        */}
      <div className="flex flex-wrap justify-center gap-sm">
        <button className={QUIET} onClick={() => setPanel(panel === "goal" ? "none" : "goal")}>
          {COPY.teacher.student.giveGoal}
        </button>
        <button
          className="px-md py-sm text-small text-muted"
          onClick={() => setPanel(panel === "edit" ? "none" : "edit")}
        >
          {COPY.teacher.student.edit}
        </button>
        <button
          className="px-md py-sm text-small text-muted"
          onClick={() => setPanel(panel === "remove" ? "none" : "remove")}
        >
          {COPY.teacher.student.remove}
        </button>
      </div>

      {panel === "goal" && (
        <GivePanel
          studentId={studentId}
          name={student.firstName ?? name}
          catalogue={state.catalogue}
          onDone={async () => {
            setPanel("none");
            await load();
          }}
        />
      )}
      {panel === "edit" && (
        <EditPanel
          studentId={studentId}
          firstName={student.firstName ?? ""}
          lastName={student.lastName ?? ""}
          onDone={async () => {
            setPanel("none");
            await load();
          }}
          onCancel={() => setPanel("none")}
        />
      )}
      {panel === "remove" && (
        <RemovePanel
          studentId={studentId}
          onRemoved={onRemoved}
          onCancel={() => setPanel("none")}
        />
      )}
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-md bg-card px-sm py-sm text-center shadow-card">
      <b className="block text-title tabular-nums">{fa(value)}</b>
      <span className="block text-caption text-muted">{label}</span>
    </div>
  );
}

/**
 * What to give this child next — the only coloured thing on the screen.
 *
 * It was a sentence in a card the same colour as everything around it. A suggestion a teacher
 * cannot act on from where they are reading it is a suggestion they will not act on, so the goal is
 * named on its own line, the game it is practised in sits under it, and the button hands it over in
 * one tap.
 */
function NextCard({
  detail,
  studentId,
  name,
  onGiven,
}: {
  detail: StudentDetail;
  studentId: string;
  name: string;
  onGiven: () => Promise<void>;
}) {
  const { current } = useClasses();
  const [busy, setBusy] = useState(false);
  const { next } = detail.student;

  if (!next.objectiveId || !next.taskId) {
    return <p className={`${CARD} mb-lg text-body`}>{COPY.teacher.student.nextNothing}</p>;
  }

  // Already asked for, so the button would be a second copy of an assignment that exists.
  const assigned = detail.objectives.find(
    (o) => o.objectiveId === next.objectiveId,
  )?.assignmentId;

  return (
    <section className="mb-lg rounded-lg bg-grape/12 px-md py-md">
      <p className="text-caption font-bold text-grape">{COPY.teacher.student.nextLabel}</p>
      <p className="mt-1 text-title">{objectiveLabel(next.objectiveId)}</p>
      <p className="mt-1 text-caption text-muted">
        {fill(COPY.teacher.student.nextIn, {
          game: taskLabel(next.taskId),
          level: fa(next.startAt ?? 1),
        })}
      </p>
      <button
        className={`${ACTION} mt-md w-full`}
        disabled={busy || assigned !== null}
        onClick={() => {
          setBusy(true);
          void assignObjective({
            objectiveId: next.objectiveId as string,
            classId: current?.classId ?? "",
            students: [studentId],
          })
            // Reloaded either way: the server is the authority on what is assigned, so a failed
            // write and a succeeded one both resolve by re-reading.
            .then(onGiven, onGiven)
            .finally(() => setBusy(false));
        }}
      >
        {busy
          ? COPY.enter.working
          : assigned
            ? COPY.teacher.student.nextGiven
            : fill(COPY.teacher.student.nextGive, { name })}
      </button>
    </section>
  );
}

/**
 * One area of the week, as a bar of its own objectives.
 *
 * Every segment is one objective — learnt, in progress, or not started — so the row says how far
 * the area has got AND how much of it there is, which a count alone does not. Tapping opens the
 * objectives themselves, including the ones nobody has touched: a teacher has to be able to see
 * what is left, not only what is done.
 */
function StrandRow({
  strand,
  entries,
  open,
  onToggle,
}: {
  strand: string;
  entries: ObjectiveProgress[];
  open: boolean;
  onToggle: () => void;
}) {
  const done = entries.filter((e) => e.state === "mastered").length;

  return (
    <div className="border-edge [&+&]:border-t [&+&]:pt-sm [&+&]:mt-sm">
      <button className="flex w-full items-center gap-sm text-start" onClick={onToggle}>
        <span className="w-[68px] shrink-0 text-body font-bold">{strandLabel(strand)}</span>
        <span className="flex min-w-0 flex-1 gap-[3px]">
          {entries.map((entry) => (
            <i
              key={entry.objectiveId}
              className={`h-[10px] min-w-[3px] flex-1 rounded-xs ${
                entry.state === "mastered"
                  ? "bg-correct"
                  : entry.state === "practising"
                    ? "bg-sky"
                    : "bg-edge"
              }`}
            />
          ))}
        </span>
        <span className="shrink-0 text-caption text-muted tabular-nums">
          {fill(COPY.teacher.student.strandCount, {
            done: fa(done),
            total: fa(entries.length),
          })}
        </span>
      </button>

      {open && (
        <ul className="mt-sm flex flex-col gap-sm">
          {entries.map((entry) => (
            <ObjectiveRow key={entry.objectiveId} entry={entry} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ObjectiveRow({ entry }: { entry: ObjectiveProgress }) {
  const stuck =
    entry.state === "practising" &&
    entry.trials >= STUCK_AFTER_TRIALS &&
    entry.bestStreak < entry.needStreak;

  return (
    <li className={stuck ? "border-s-[3px] border-sunny ps-sm" : undefined}>
      <span className="flex items-baseline justify-between gap-sm">
        <span className={`text-body ${entry.state === "untouched" ? "text-muted" : ""}`}>
          {objectiveLabel(entry.objectiveId)}
        </span>
        {entry.state === "mastered" ? (
          <span className="shrink-0 text-caption text-correct">
            {COPY.teacher.student.mastered}
          </span>
        ) : entry.state === "untouched" ? (
          <span className="shrink-0 text-caption text-muted">
            {COPY.teacher.student.notStarted}
          </span>
        ) : (
          /*
           * The run, as dots rather than as a sentence.
           *
           * The sentence form is accurate and has to be decoded; two filled circles out of three are
           * read at a glance, which is what a row in a list of seventeen needs.
           */
          <span className="flex shrink-0 items-center gap-[3px]">
            {Array.from({ length: entry.needStreak }, (_, i) => (
              <i
                key={i}
                className={`size-[9px] rounded-pill ${
                  i < entry.bestStreak ? "bg-sky" : "bg-edge"
                }`}
              />
            ))}
          </span>
        )}
      </span>
      {entry.state !== "untouched" && (
        <span className="mt-1 block text-caption text-muted tabular-nums">
          {stuck
            ? fill(COPY.teacher.student.stuckNote, {
                count: fa(entry.trials),
                need: fa(entry.needStreak),
              })
            : fill(COPY.teacher.student.trials, { count: fa(entry.trials) })}
          {entry.assignmentId && ` · ${COPY.teacher.student.assignedByYou}`}
        </span>
      )}
    </li>
  );
}

/**
 * Giving one objective to one child — the escape hatch beside the suggestion.
 *
 * Grouped by strand and in catalogue order, which is teaching order. Every objective is offered, not
 * only the ones the recommender would pick: `requires` orders suggestions and locks nothing, and this
 * is the screen where a teacher who knows something we do not acts on it.
 */
function GivePanel({
  studentId,
  name,
  catalogue,
  onDone,
}: {
  studentId: string;
  name: string;
  catalogue: Catalogue | null;
  onDone: () => Promise<void>;
}) {
  const { current } = useClasses();
  const classId = current?.classId ?? "";
  const [busy, setBusy] = useState<string | null>(null);

  if (!catalogue) {
    return <p className={`${CARD} mt-lg text-center text-muted`}>{COPY.teacher.loading}</p>;
  }

  const byStrand = new Map<string, typeof catalogue.objectives>();
  for (const objective of catalogue.objectives) {
    const list = byStrand.get(objective.strand) ?? [];
    list.push(objective);
    byStrand.set(objective.strand, list);
  }

  return (
    <section className={`${CARD} mt-lg`}>
      <h2 className="mb-sm text-title">{COPY.teacher.student.giveGoal}</h2>
      <p className="mb-md text-caption text-muted">{COPY.teacher.goals.orderNote}</p>
      {[...byStrand.entries()].map(([strand, list]) => (
        <div key={strand} className="mb-md">
          <h3 className="mb-sm text-small text-muted">{strandLabel(strand)}</h3>
          <div className="flex flex-wrap gap-sm">
            {list.map((objective) => (
              <button
                key={objective.id}
                disabled={busy !== null}
                onClick={() => {
                  setBusy(objective.id);
                  void assignObjective({
                    objectiveId: objective.id,
                    // The class the panel is being viewed inside. The server checks the child is in
                    // it, so a stale switcher is refused rather than silently assigning elsewhere.
                    classId,
                    students: [studentId],
                  }).then(onDone, onDone);
                }}
                className={QUIET}
                title={fill(COPY.teacher.student.giveGoalTo, { name })}
              >
                {busy === objective.id ? COPY.enter.working : objectiveLabel(objective.id)}
              </button>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function EditPanel({
  studentId,
  firstName,
  lastName,
  onDone,
  onCancel,
}: {
  studentId: string;
  firstName: string;
  lastName: string;
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const [first, setFirst] = useState(firstName);
  const [last, setLast] = useState(lastName);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className={`${CARD} mt-lg flex flex-col gap-md`}
      onSubmit={(e) => {
        e.preventDefault();
        if (busy || !first.trim() || !last.trim()) return;
        setBusy(true);
        void renameStudent(studentId, { firstName: first, lastName: last }).then(onDone, onDone);
      }}
    >
      <h2 className="text-title">{COPY.teacher.student.editTitle}</h2>
      <label className="flex flex-col gap-1 text-small text-muted">
        {COPY.teacher.addStudent.firstName}
        <input className={FIELD} value={first} onChange={(e) => setFirst(e.target.value)} />
      </label>
      <label className="flex flex-col gap-1 text-small text-muted">
        {COPY.teacher.addStudent.lastName}
        <input className={FIELD} value={last} onChange={(e) => setLast(e.target.value)} />
      </label>
      <button type="submit" className={ACTION} disabled={busy || !first.trim() || !last.trim()}>
        {busy ? COPY.enter.working : COPY.teacher.student.save}
      </button>
      <button type="button" onClick={onCancel} className="py-sm text-small text-muted">
        {COPY.teacher.student.cancel}
      </button>
    </form>
  );
}

/**
 * Removal, asked twice.
 *
 * Irreversible and outward-facing — a child's name is erased — so the warning states exactly what goes
 * and what stays before the button that does it appears. A single tap on a list row would be the wrong
 * shape for this entirely.
 */
function RemovePanel({
  studentId,
  onRemoved,
  onCancel,
}: {
  studentId: string;
  onRemoved: () => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);

  return (
    <section className={`${CARD} mt-lg flex flex-col gap-md`}>
      <h2 className="text-title">{COPY.teacher.student.removeTitle}</h2>
      <p className="text-small text-muted">{COPY.teacher.student.removeWarning}</p>
      <button
        className="rounded-pill bg-wrong px-md py-sm text-small text-card disabled:opacity-60"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void removeStudent(studentId).then(onRemoved, () => setBusy(false));
        }}
      >
        {busy ? COPY.enter.working : COPY.teacher.student.removeConfirm}
      </button>
      <button onClick={onCancel} className="py-sm text-small text-muted">
        {COPY.teacher.student.cancel}
      </button>
    </section>
  );
}
